import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

/**
 * VDM Status enum (matching backend model)
 */
export const VDMStatusSchema = z.enum([
  "created",
  "pending",
  "accepted",
  "rejected",
]);

/**
 * Pagination schema
 */
export const VDMPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Supplier summary for VDM response
 */
const VDMSupplierSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * VDM schema for RPC responses
 */
export const VDMSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  gentlyVdmNumber: z.int(),
  vdmNumber: z.int().nullable().optional(),
  grossAmount: DecimalStringSchema,
  status: VDMStatusSchema,
  calculateTax: z.boolean(),
  shippingProvince: z.string().nullable().optional(),
  specialInstructions1: z.string().nullable().optional(),
  specialInstructions2: z.string().nullable().optional(),
  specialInstructions3: z.string().nullable().optional(),
  emailToAddress: z.string().nullable().optional(),
  emailCcAddress: z.string().nullable().optional(),
  companyCode: z.string(),
  divisionCode: z.string().nullable().optional(),
  branchCode: z.string().nullable().optional(),
  gstHstAmount: DecimalStringSchema.nullable().optional(),
  qstPstAmount: DecimalStringSchema.nullable().optional(),
  totalTaxAmount: DecimalStringSchema.nullable().optional(),
  totalAmount: DecimalStringSchema.nullable().optional(),
  error: z.string().nullable().optional(),
  supplierId: UUIDSchema,
  supplier: VDMSupplierSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

/**
 * List VDMs input schema with filters
 */
export const ListVDMsInputSchema = z.object({
  // Filter by status
  status: z.array(VDMStatusSchema).optional(),
  // Filter by supplier
  supplierId: UUIDSchema.optional(),
  // Filter by company code
  companyCode: z.string().optional(),
  // Filter by gently VDM number
  gentlyVdmNumber: z.int().optional(),
  // Filter by external VDM number
  vdmNumber: z.int().optional(),
  // Date filters
  createdAtFrom: z.string().optional(),
  createdAtTo: z.string().optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List VDMs response schema
 */
export const ListVDMsResponseSchema = z.object({
  vdms: z.array(VDMSchema),
  pagination: VDMPaginationSchema,
});

/**
 * Get single VDM input schema
 */
export const GetVDMInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Get VDM response schema
 */
export const GetVDMResponseSchema = VDMSchema;

/**
 * VDM Activity Type enum (matching backend model)
 */
export const VDMActivityTypeSchema = z.enum([
  "created",
  "sent_to_supplier",
  "supplier_replied",
  "reply_sent",
  "accepted",
  "rejected",
  "status_changed",
  "error_occurred",
  "resent",
  "manually_updated",
  "comment_added",
]);

/**
 * User summary for activity response
 */
const ActivityUserSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * VDM Activity schema for RPC responses
 */
export const VDMActivitySchema = z.object({
  id: UUIDSchema,
  vdmId: UUIDSchema,
  activityType: VDMActivityTypeSchema,
  userId: UUIDSchema.nullable().optional(),
  user: ActivityUserSchema.nullable().optional(),
  inboundEmailId: UUIDSchema.nullable().optional(),
  eventAt: z.coerce.date(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
});

/**
 * List VDM Activities input schema
 */
export const ListVDMActivitiesInputSchema = z.object({
  vdmId: UUIDSchema,
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List VDM Activities response schema
 */
export const ListVDMActivitiesResponseSchema = z.object({
  activities: z.array(VDMActivitySchema),
  pagination: VDMPaginationSchema,
});

/**
 * Add Comment input schema
 */
export const AddVDMCommentInputSchema = z.object({
  vdmId: UUIDSchema,
  comment: z.string().min(1).max(2000),
});

/**
 * Add Comment response schema
 */
export const AddVDMCommentResponseSchema = VDMActivitySchema;

/**
 * VDM RPC Contracts
 */
export const vdmContracts = {
  list: oc.input(ListVDMsInputSchema).output(ListVDMsResponseSchema).route({
    method: "GET",
  }),
  get: oc.input(GetVDMInputSchema).output(GetVDMResponseSchema).route({
    method: "GET",
  }),
  listActivities: oc
    .input(ListVDMActivitiesInputSchema)
    .output(ListVDMActivitiesResponseSchema)
    .route({
      method: "GET",
    }),
  addComment: oc
    .input(AddVDMCommentInputSchema)
    .output(AddVDMCommentResponseSchema)
    .route({
      method: "POST",
    }),
} as const;

// Type exports
export type VDMStatus = z.infer<typeof VDMStatusSchema>;
export type VDM = z.infer<typeof VDMSchema>;
export type ListVDMsInput = z.infer<typeof ListVDMsInputSchema>;
export type ListVDMsResponse = z.infer<typeof ListVDMsResponseSchema>;
export type GetVDMInput = z.infer<typeof GetVDMInputSchema>;
export type GetVDMResponse = z.infer<typeof GetVDMResponseSchema>;
export type VDMActivityType = z.infer<typeof VDMActivityTypeSchema>;
export type VDMActivity = z.infer<typeof VDMActivitySchema>;
export type ListVDMActivitiesInput = z.infer<
  typeof ListVDMActivitiesInputSchema
>;
export type ListVDMActivitiesResponse = z.infer<
  typeof ListVDMActivitiesResponseSchema
>;
export type AddVDMCommentInput = z.infer<typeof AddVDMCommentInputSchema>;
export type AddVDMCommentResponse = z.infer<typeof AddVDMCommentResponseSchema>;
